import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { authService, AdminUser, Company } from "../services/auth";
import { useAuth } from "../context/AuthContext";
import { useCohorts } from "../hooks/useStudyQueries";
import { useProjectsQuery } from "../hooks/useLabelingQueries";
import { useClickOutside } from "../hooks/useClickOutside";
import {
  FaSpinner,
  FaPlus,
  FaEdit,
  FaLink,
  FaCheck,
  FaTimes,
  FaQuestionCircle,
  FaRedo,
  FaTrash,
} from "../utils/icons";
import {
  Alert,
  AlertDescription,
  Input,
  Label,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/design-system/components/react";
import { isInternalRole } from "../services/roles";
import { findGrant } from "../services/grants";
import type { Grant } from "../services/auth";

/** Format an ISO date string to a readable locale date. */
function formatGrantDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * A read-only chip that optionally shows grant metadata on hover via a Radix
 * tooltip. When no matching grant is found (e.g. pre-backfill data), the chip
 * renders without a tooltip wrapper.
 */
const GrantChip: React.FC<{
  id: string;
  grant: Grant | undefined;
  inherited?: boolean;
  className?: string;
}> = ({
  id,
  grant,
  inherited = false,
  className = "max-w-full break-all px-1.5 py-0.5 text-xs bg-gray-700 rounded",
}) => {
  // Company-inherited cohort — no per-user grant record. Mark it distinctly and
  // explain (unchecking it in the editor writes a per-user block).
  if (inherited) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`${className} cursor-default border border-signal-stroke-soft`}
              data-testid={`grant-chip-${id}`}
            >
              {id} <span className="text-signal-text-tint">· via company</span>
            </span>
          </TooltipTrigger>
          <TooltipContent
            className="text-xs space-y-0.5 bg-gray-800 border-gray-700 text-gray-200"
            data-testid={`grant-tooltip-${id}`}
          >
            <div>Inherited from the user&apos;s company</div>
            <div>Uncheck to block it for just this user</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (!grant) {
    return <span className={className}>{id}</span>;
  }

  const grantedBy = grant.granted_by ?? "unknown";
  const grantedAt = grant.granted_at ? formatGrantDate(grant.granted_at) : "date unknown";
  const expires = grant.expires ? formatGrantDate(grant.expires) : "never";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`${className} cursor-default`} data-testid={`grant-chip-${id}`}>
            {id}
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="text-xs space-y-0.5 bg-gray-800 border-gray-700 text-gray-200"
          data-testid={`grant-tooltip-${id}`}
        >
          <div>Granted by {grantedBy}</div>
          <div>{grantedAt}</div>
          <div>Expires: {expires}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const ROLE_OPTIONS = [
  { value: "annotator", label: "Annotator" },
  { value: "demo_viewer", label: "Demo Viewer" },
  { value: "reviewer", label: "Reviewer" },
];

// Reviewers use the reader (inference) cohort list; the other external roles
// pick from the labeling cohort list. Defined here so InviteModal can switch
// its cohort source by role without two parallel modals.
const READER_COHORT_ROLES: ReadonlySet<string> = new Set(["reviewer"]);

const ROLE_FILTERS = ["all", "admin", "internal", "annotator", "demo_viewer", "reviewer"];

// `client` is the legacy name for internal A2Z staff; surface both as "internal".
export const displayRole = (role: string): string => (role === "client" ? "internal" : role);

/** Keep internal inviter addresses compact in the user table. */
export const displayInvitedBy = (email: string | null | undefined): string =>
  email?.replace(/@a2zradiology\.ai$/i, "@a2z") || "—";

const DEFAULT_INVITE_TTL_DAYS = 7;
const MIN_INVITE_TTL_DAYS = 1;
const MAX_INVITE_TTL_DAYS = 60;

/** Fetches available project IDs and cohort values for autocomplete */
const useAvailableOptions = () => {
  const { data: projectsData } = useProjectsQuery();
  const projectOptions = (projectsData?.projects || []).map((p) => p.project_id);
  const { data: cohortsData } = useQuery({
    queryKey: ["admin-cohorts-list"],
    queryFn: async () => {
      const { token } = authService.getStoredAuth();
      const res = await axios.get(`${getApiBaseUrl()}/api/labeling/cohorts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.cohorts as string[];
    },
    staleTime: 60_000,
  });
  return { projectOptions, cohortOptions: cohortsData || [] };
};

/** Multi-select tag input with autocomplete dropdown */
const TagSelect: React.FC<{
  options: string[];
  selected: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}> = ({ options, selected, onChange, placeholder }) => {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeDropdown = useCallback(() => setOpen(false), []);
  useClickOutside(ref, closeDropdown);

  const filtered = options.filter(
    (o) => !selected.includes(o) && o.toLowerCase().includes(input.toLowerCase()),
  );

  const addTag = (tag: string) => {
    if (!selected.includes(tag)) onChange([...selected, tag]);
    setInput("");
  };

  const removeTag = (tag: string) => onChange(selected.filter((t) => t !== tag));

  return (
    <div ref={ref} className="relative">
      <div className="flex flex-wrap gap-1 p-1.5 bg-bg-2/50 border border-border-strong rounded min-h-[36px]">
        {selected.map((tag) => (
          <span
            key={tag}
            className="flex items-center gap-1 px-1.5 py-0.5 text-xs bg-signal-tint text-signal-text-tint rounded"
          >
            {tag}
            <button onClick={() => removeTag(tag)} className="hover:text-white">
              <FaTimes className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && input.trim()) {
              e.preventDefault();
              addTag(input.trim());
            }
            if (e.key === "Backspace" && !input && selected.length) {
              removeTag(selected[selected.length - 1]);
            }
          }}
          placeholder={selected.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[80px] bg-transparent text-white text-xs outline-none px-1"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto bg-bg-2 border border-border-strong rounded shadow-lg">
          {filtered.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                addTag(opt);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs text-fg-muted hover:bg-white/10 hover:text-white"
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

import { groupProjects, US3D_REGEX } from "../utils/studyGroups";
import { postAuthDestination } from "../utils/reviewerNav";

interface Us3dPhase {
  phaseKey: string;
  label: string;
  batches: { batchLabel: string; projectId: string }[];
}

const groupUs3dByPhase = (projectIds: string[]): Us3dPhase[] => {
  const phases = new Map<string, { batchLabel: string; projectId: string }[]>();
  for (const id of projectIds) {
    const m = id.match(US3D_REGEX);
    if (!m) continue;
    const [, phase, batch] = m;
    if (!phases.has(phase)) phases.set(phase, []);
    phases.get(phase)!.push({ batchLabel: batch, projectId: id });
  }
  return Array.from(phases.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([phaseKey, batches]) => ({
      phaseKey,
      label: `Phase ${phaseKey}`,
      batches: batches.sort((a, b) => a.batchLabel.localeCompare(b.batchLabel)),
    }));
};

/** Grouped project selector with collapsible study groups, checkboxes, and search filter */
const GroupedProjectSelect: React.FC<{
  options: string[];
  selected: string[];
  onChange: (projects: string[]) => void;
  collapsible?: boolean;
}> = ({ options, selected, onChange, collapsible = false }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(!collapsible);
  const selectorRef = useRef<HTMLDivElement>(null);
  useClickOutside(selectorRef, () => {
    if (collapsible) setIsOpen(false);
  });
  const searchLower = search.toLowerCase();

  const groups = useMemo(() => groupProjects(options), [options]);
  const us3dPhases = useMemo(() => {
    const us3dGroup = groups.find((g) => g.key === "us3d");
    return us3dGroup ? groupUs3dByPhase(us3dGroup.projects) : [];
  }, [groups]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (projectId: string) => {
    onChange(
      selected.includes(projectId)
        ? selected.filter((p) => p !== projectId)
        : [...selected, projectId],
    );
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const totalSelected = selected.length;

  // When searching, flatten all projects and filter by search term; group
  // headers are hidden. When not searching, render the normal grouped/collapsible view.
  const isFiltering = searchLower.length > 0;
  const filteredFlatProjects = isFiltering
    ? options.filter((p) => p.toLowerCase().includes(searchLower))
    : [];

  const projectList = (
    <div className="bg-bg-2 border border-border-strong rounded overflow-hidden">
      {/* Search input — mirrors TagSelect's filter input style */}
      <div className="px-2 py-1.5 border-b border-border-strong/60">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter projects..."
          data-testid="project-select-search"
          className="w-full bg-transparent text-white text-xs outline-none px-1 placeholder:text-fg-muted"
        />
      </div>
      <div className="max-h-52 overflow-y-auto">
        {isFiltering ? (
          filteredFlatProjects.length > 0 ? (
            <div className="pb-1">
              {filteredFlatProjects.map((projectId) => (
                <label
                  key={projectId}
                  className="flex items-center gap-2 px-3 pl-4 py-1 text-xs text-fg-muted hover:bg-white/5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(projectId)}
                    onChange={() => toggle(projectId)}
                    className="rounded border-border-strong bg-surface-2 text-signal-text-tint focus:ring-primary/30 w-3.5 h-3.5"
                  />
                  <span className="truncate">{projectId}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="px-3 py-3 text-xs text-fg-muted">No matching projects</div>
          )
        ) : (
          <>
            {groups.map((group) => {
              const isOpen = expanded[group.key] ?? false;
              const selectedInGroup = group.projects.filter((p) => selected.includes(p)).length;
              const isUs3d = group.key === "us3d";
              const phases = isUs3d ? us3dPhases : [];
              return (
                <div key={group.key}>
                  <button
                    type="button"
                    onClick={() => toggleExpand(group.key)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-fg-muted text-[10px]">{isOpen ? "▼" : "▶"}</span>
                      <span className="font-medium text-fg-muted">{group.label}</span>
                    </div>
                    {selectedInGroup > 0 && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-signal-tint text-signal-text-tint rounded">
                        {selectedInGroup}
                      </span>
                    )}
                  </button>
                  {isOpen && isUs3d && (
                    <div className="pb-1">
                      {phases.map((phase) => {
                        const phaseExpandKey = `${group.key}:${phase.phaseKey}`;
                        const phaseOpen = expanded[phaseExpandKey] ?? false;
                        const selectedInPhase = phase.batches.filter((b) =>
                          selected.includes(b.projectId),
                        ).length;
                        return (
                          <div key={phase.phaseKey}>
                            <button
                              type="button"
                              onClick={() => toggleExpand(phaseExpandKey)}
                              className="w-full flex items-center justify-between px-3 pl-7 py-1.5 text-xs hover:bg-white/5 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-fg-muted text-[10px]">
                                  {phaseOpen ? "▼" : "▶"}
                                </span>
                                <span className="text-fg-muted">{phase.label}</span>
                              </div>
                              {selectedInPhase > 0 && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-signal-tint text-signal-text-tint rounded">
                                  {selectedInPhase}
                                </span>
                              )}
                            </button>
                            {phaseOpen && (
                              <div className="grid grid-cols-4 gap-1 px-3 pl-12 pr-3 py-1.5">
                                {phase.batches.map((batch) => (
                                  <label
                                    key={batch.projectId}
                                    className="flex items-center gap-1.5 px-1.5 py-1 text-xs text-fg-muted hover:bg-white/5 rounded cursor-pointer"
                                    title={batch.projectId}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selected.includes(batch.projectId)}
                                      onChange={() => toggle(batch.projectId)}
                                      className="rounded border-border-strong bg-surface-2 text-signal-text-tint focus:ring-primary/30 w-3.5 h-3.5"
                                    />
                                    <span className="font-mono">{batch.batchLabel}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {isOpen && !isUs3d && (
                    <div className="pb-1">
                      {group.projects.map((projectId) => (
                        <label
                          key={projectId}
                          className="flex items-center gap-2 px-3 pl-7 py-1 text-xs text-fg-muted hover:bg-white/5 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(projectId)}
                            onChange={() => toggle(projectId)}
                            className="rounded border-border-strong bg-surface-2 text-signal-text-tint focus:ring-primary/30 w-3.5 h-3.5"
                          />
                          <span className="truncate">{projectId}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {groups.length === 0 && (
              <div className="px-3 py-3 text-xs text-fg-muted">No projects available</div>
            )}
          </>
        )}
      </div>
      {totalSelected > 0 && (
        <div className="px-3 py-1.5 border-t border-border-strong/50 text-[10px] text-fg-muted">
          {totalSelected} project{totalSelected !== 1 ? "s" : ""} selected
        </div>
      )}
    </div>
  );

  if (!collapsible) return projectList;

  return (
    <div ref={selectorRef}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-label="Select projects"
        data-testid="project-select-trigger"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center justify-between rounded border border-border-strong bg-bg-2 px-3 py-2 text-left text-xs text-fg-muted"
      >
        <span>
          {totalSelected
            ? `${totalSelected} project${totalSelected === 1 ? "" : "s"} selected`
            : "Select projects…"}
        </span>
        <span aria-hidden="true" className="text-[10px]">
          {isOpen ? "▲" : "▼"}
        </span>
      </button>
      {isOpen && <div className="mt-1">{projectList}</div>}
    </div>
  );
};

const AdminUserManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const { userId: currentAdminId, token } = useAuth();

  const { projectOptions, cohortOptions: labelingCohortOptions } = useAvailableOptions();
  const { data: readerCohortsData } = useCohorts(token);
  const readerCohortOptions = useMemo(
    () => (readerCohortsData || []).map((c) => c.run_id),
    [readerCohortsData],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => authService.getAdminUsers(),
  });

  const { data: companyOptions } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: () => authService.getCompanies(),
  });

  const users = data?.users || [];
  const filteredUsers = users.filter((user) => {
    const matchesRole = roleFilter === "all" || displayRole(user.user_type) === roleFilter;
    const matchesCompany =
      companyFilter === "all" ||
      (companyFilter === "unassigned" ? !user.company_id : user.company_id === companyFilter);
    return matchesRole && matchesCompany;
  });

  return (
    <div className="flex-1 p-6 text-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold">User Management</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="p-1.5 text-fg-muted hover:text-white rounded hover:bg-white/5 transition-colors"
              title="User types & permissions"
            >
              <FaQuestionCircle className="w-4 h-4" />
            </button>
            <Button onClick={() => setShowInviteModal(true)} className="flex items-center gap-2">
              <FaPlus className="w-3 h-3" />
              Invite User
            </Button>
          </div>
        </div>

        {showHelp && (
          <div className="mb-4 p-4 bg-surface-2 border border-border-strong/50 rounded text-sm space-y-2">
            <div className="flex justify-between items-start">
              <h3 className="font-medium text-fg mb-2">User Types & Permissions</h3>
              <button onClick={() => setShowHelp(false)} className="text-fg-muted hover:text-white">
                <FaTimes className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
              <div>
                <span className="text-fg font-medium">Admin</span>
                <p className="text-fg-muted mt-0.5">
                  Full access. Manage users, view analytics, switch accounts, all projects and
                  cohorts.
                </p>
              </div>
              <div>
                <span className="text-fg font-medium">Internal</span>
                <p className="text-fg-muted mt-0.5">
                  Internal user. View all studies and projects. No admin menu or user management.
                </p>
              </div>
              <div>
                <span className="text-fg font-medium">Annotator</span>
                <p className="text-fg-muted mt-0.5">
                  External. Invited via email. Must sign Terms of Use. Restricted to assigned
                  projects and cohorts.
                </p>
              </div>
              <div>
                <span className="text-fg font-medium">Demo Viewer</span>
                <p className="text-fg-muted mt-0.5">
                  External. Invited via email. Must sign Platform NDA. Restricted to assigned
                  projects and cohorts. Read-only.
                </p>
              </div>
              <div>
                <span className="text-fg font-medium">Reviewer</span>
                <p className="text-fg-muted mt-0.5">
                  External partner radiologist. Invited via email. Must sign Platform NDA. Scoped to
                  specific reader cohorts; right/wrong/borderline disposition per case.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-2">
            {ROLE_FILTERS.map((role) => (
              <button
                key={role}
                onClick={() => setRoleFilter(role)}
                aria-pressed={roleFilter === role}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  roleFilter === role
                    ? "bg-signal-tint text-signal-text-tint"
                    : "bg-surface-2 text-fg-muted hover:text-white"
                }`}
              >
                {role === "all" ? "All" : role.replace("_", " ")}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-fg-muted">
            Company
            <select
              aria-label="Filter by company"
              value={companyFilter}
              onChange={(event) => setCompanyFilter(event.target.value)}
              className="rounded border border-border-strong bg-bg-2 px-2 py-1 text-xs text-fg"
            >
              <option value="all">All companies</option>
              <option value="unassigned">Unassigned</option>
              {(companyOptions ?? []).map((company) => (
                <option key={company.id} value={company.id}>
                  {company.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4" data-testid="admin-users-error">
            <AlertDescription>Failed to load users.</AlertDescription>
          </Alert>
        )}

        {/* testid'd so E2E can gate on *this* loader. Seven elements in this file carry
            `animate-spin` (row-level Save / Assume / Resend spinners included), so a
            `.animate-spin` CSS locator is ambiguous — and CSS-class locators are banned
            by CLAUDE.md anyway. */}
        {isLoading ? (
          <div className="flex justify-center py-12" data-testid="admin-users-loading">
            <FaSpinner className="animate-spin h-6 w-6 text-fg-muted" />
          </div>
        ) : (
          <div className="bg-surface-2 border border-border-strong/50 rounded overflow-x-auto">
            <table className="w-full table-fixed text-sm" data-testid="admin-users-table">
              <colgroup>
                <col className="w-[15%]" />
                <col className="w-[6%]" />
                <col className="w-[9%]" />
                <col className="w-[15%]" />
                <col className="w-[14%]" />
                <col className="w-[10%]" />
                <col className="w-[11%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border-strong text-fg-muted text-xs uppercase">
                  <th className="px-3 py-3 text-left">Email / ID</th>
                  <th className="px-3 py-3 text-left">Role</th>
                  <th className="px-3 py-3 text-left">Company</th>
                  <th className="px-3 py-3 text-left">Cohorts</th>
                  <th className="px-3 py-3 text-left">Projects</th>
                  <th className="px-3 py-3 text-left">Capabilities</th>
                  <th className="px-3 py-3 text-left">Agreement</th>
                  <th className="px-3 py-3 text-left">Invited By</th>
                  <th className="px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <UserRow
                    key={user.user_id}
                    user={user}
                    projectOptions={projectOptions}
                    cohortOptions={
                      READER_COHORT_ROLES.has(user.user_type)
                        ? readerCohortOptions
                        : labelingCohortOptions
                    }
                    companyOptions={companyOptions ?? []}
                    currentAdminId={currentAdminId}
                  />
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-fg-muted">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showInviteModal && (
        <InviteModal
          projectOptions={projectOptions}
          labelingCohortOptions={labelingCohortOptions}
          readerCohortOptions={readerCohortOptions}
          companyOptions={companyOptions ?? []}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
          }}
        />
      )}
    </div>
  );
};

const InlineTagEditor: React.FC<{
  values: string[] | undefined;
  options: string[];
  userType: string;
  onSave: (newValues: string[]) => Promise<void>;
  emptyLabel?: string;
  useGrouped?: boolean;
  grants?: Grant[];
  grantType?: "cohort" | "project";
  /** ids that are company-inherited (marked distinctly; present in `values` so a
   *  save carries them and the backend only blocks genuinely-removed ones). */
  inheritedIds?: Set<string>;
}> = ({
  values,
  options,
  userType,
  onSave,
  emptyLabel,
  useGrouped,
  grants,
  grantType,
  inheritedIds,
}) => {
  const isInternal = isInternalRole(userType);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleEdit = () => {
    setDraft(values || []);
    setSaveError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (err: unknown) {
      const message = axios.isAxiosError(err)
        ? err.response?.data?.detail || err.message
        : err instanceof Error
          ? err.message
          : "Failed to save";
      setSaveError(String(message));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="min-w-[160px]">
        {useGrouped ? (
          <GroupedProjectSelect options={options} selected={draft} onChange={setDraft} />
        ) : (
          <TagSelect
            options={options}
            selected={draft}
            onChange={setDraft}
            placeholder="Type to search..."
          />
        )}
        <div className="flex gap-1 mt-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-1.5 py-0.5 text-xs text-signal-text-tint hover:text-signal-text-tint bg-signal-tint rounded"
          >
            {saving ? <FaSpinner className="animate-spin w-3 h-3" /> : "Save"}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-1.5 py-0.5 text-xs text-fg-muted hover:text-fg bg-surface-2/50 rounded"
          >
            Cancel
          </button>
        </div>
        {saveError && (
          <div className="mt-1 text-[11px] text-warn bg-warn-tint border border-warn rounded px-1.5 py-1">
            {saveError}
          </div>
        )}
      </div>
    );
  }

  const defaultEmpty = emptyLabel || (isInternal ? "All" : "None");

  return (
    <div className="flex min-w-0 items-center gap-1.5 group">
      {values?.length ? (
        <div className="flex min-w-0 flex-wrap gap-1">
          {values.map((v) => (
            <GrantChip
              key={v}
              id={v}
              inherited={inheritedIds?.has(v)}
              grant={grants && grantType ? findGrant(grants, grantType, v) : undefined}
            />
          ))}
        </div>
      ) : (
        <span className="text-xs text-fg-muted">{defaultEmpty}</span>
      )}
      {!isInternal && (
        <button
          onClick={handleEdit}
          className="opacity-0 group-hover:opacity-100 p-0.5 text-fg-muted hover:text-white transition-opacity"
        >
          <FaEdit className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const CopyInviteLinkButton: React.FC<{ token: string; email: string }> = ({ token, email }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    const url = `${window.location.origin}/set-password?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
        copied
          ? "text-signal-text-tint bg-signal-tint"
          : "text-fg-muted hover:text-white hover:bg-white/5"
      }`}
    >
      {copied ? <FaCheck className="w-3 h-3" /> : <FaLink className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const ResendInviteButton: React.FC<{
  userId: string;
  onResent: (newToken: string) => void;
}> = ({ userId, onResent }) => {
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

  const handleResend = async () => {
    setState("sending");
    try {
      const data = await authService.resendInvite(userId);
      onResent(data.invite_token);
      setState("done");
      setTimeout(() => setState("idle"), 2000);
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 2000);
    }
  };

  return (
    <button
      onClick={handleResend}
      disabled={state === "sending"}
      className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
        state === "done"
          ? "text-signal-text-tint bg-signal-tint"
          : state === "error"
            ? "text-warn bg-warn-tint"
            : "text-fg-muted hover:text-white hover:bg-white/5"
      }`}
    >
      {state === "sending" ? (
        <FaSpinner className="w-3 h-3 animate-spin" />
      ) : state === "done" ? (
        <FaCheck className="w-3 h-3" />
      ) : (
        <FaRedo className="w-3 h-3" />
      )}
      {state === "done" ? "Resent" : state === "error" ? "Failed" : "Resend"}
    </button>
  );
};

const UserRow: React.FC<{
  user: AdminUser;
  projectOptions: string[];
  cohortOptions: string[];
  companyOptions: Company[];
  currentAdminId: string | null;
}> = ({ user, projectOptions, cohortOptions, companyOptions, currentAdminId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  const [localToken, setLocalToken] = useState(user.invite_token);
  const [deleting, setDeleting] = useState(false);
  const [togglingDisabled, setTogglingDisabled] = useState(false);
  const [assuming, setAssuming] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const [savingCap, setSavingCap] = useState(false);
  const [assumeOpen, setAssumeOpen] = useState(false);
  const [assumeError, setAssumeError] = useState<string | null>(null);
  const assumeRef = useRef<HTMLDivElement>(null);
  useClickOutside(assumeRef, () => {
    if (!assuming) {
      setAssumeOpen(false);
      setAssumeError(null);
    }
  });
  const roleColors: Record<string, string> = {
    admin: "text-yellow-400",
    internal: "text-cyan-400",
    annotator: "text-green-400",
    demo_viewer: "text-purple-400",
    reviewer: "text-pink-400",
  };

  // Assume is hidden for admins (admin→admin impersonation is server-blocked),
  // for the caller themselves, and for pending users who haven't claimed yet.
  const canAssume =
    !user.disabled &&
    user.user_type !== "admin" &&
    user.user_id !== currentAdminId &&
    user.has_accepted_agreement;

  // Only external accounts can be disabled; the backend refuses internal ones.
  const canToggleDisabled = !isInternalRole(user.user_type);

  const handleToggleDisabled = async () => {
    if (togglingDisabled) return;
    const who = user.email || user.user_id;
    const question = user.disabled
      ? `Enable ${who}? They can sign in again.`
      : `Disable ${who}? Their open sessions stop working and they cannot sign in.`;
    if (!confirm(question)) return;
    setTogglingDisabled(true);
    try {
      await authService.setUserDisabled(user.user_id, !user.disabled);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.detail : null;
      alert(message || `Failed to ${user.disabled ? "enable" : "disable"} user`);
    } finally {
      setTogglingDisabled(false);
    }
  };

  const handleAssume = async () => {
    if (assuming) return;
    setAssuming(true);
    setAssumeError(null);
    try {
      const result = await authService.impersonate(user.user_id);
      setAuthState({
        token: result.token,
        refreshToken: result.refreshToken,
        userId: result.userId,
        isAdmin: result.isAdmin,
        adminUserId: result.adminUserId ?? null,
        userType: result.userType,
        agreementAccepted: result.agreementAccepted,
      });
      navigate(
        postAuthDestination({
          userType: result.userType,
          allowedCohorts: result.allowedCohorts,
        }),
      );
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.detail : null;
      setAssumeError(message || "Failed to impersonate user");
    } finally {
      setAssuming(false);
    }
  };

  // Effective cohorts = own grants ∪ (company-inherited − blocked). The editor
  // shows this set (inherited ones marked), so a save carries inherited-to-keep
  // and the backend only writes a block for cohorts the admin actually removed.
  const blockedCohorts = new Set(user.blocked_cohorts ?? []);
  const activeInherited = (user.inherited_cohorts ?? []).filter((c) => !blockedCohorts.has(c));
  const effectiveCohorts = Array.from(
    new Set([...(user.allowed_cohorts ?? []), ...activeInherited]),
  );
  const inheritedCohortIds = new Set(activeInherited);
  const capabilities = new Set(user.capabilities ?? []);

  const saveCapability = async (capability: string, enabled: boolean) => {
    const next = new Set(capabilities);
    if (enabled) next.add(capability);
    else next.delete(capability);
    setSavingCap(true);
    try {
      await authService.setUserCapabilities(user.user_id, Array.from(next));
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    } finally {
      setSavingCap(false);
    }
  };

  return (
    <tr className="border-b border-border-strong/50 hover:bg-white/5 transition-colors group">
      <td className="px-3 py-3">
        <div className="font-medium break-all">{user.email || user.user_id}</div>
        {user.email && user.email !== user.user_id && (
          <div className="text-xs text-fg-muted break-all">{user.user_id}</div>
        )}
      </td>
      <td className="px-3 py-3">
        {user.disabled && (
          <span
            className="block w-fit rounded border border-border-strong px-1.5 py-0.5 text-xs font-medium"
            title="This account cannot sign in or use existing sessions."
          >
            Disabled
          </span>
        )}
        <span
          className={`text-xs font-medium capitalize ${user.disabled ? "text-fg-muted" : roleColors[displayRole(user.user_type)] || "text-fg-muted"}`}
        >
          {user.disabled && "Stored role: "}
          {displayRole(user.user_type).replace("_", " ")}
        </span>
      </td>
      <td className="px-3 py-3">
        {editingCompany ? (
          <select
            autoFocus
            value={user.company_id || ""}
            onChange={async (e) => {
              const val = e.target.value || null;
              setEditingCompany(false);
              await authService.setUserCompany(user.user_id, val);
              queryClient.invalidateQueries({ queryKey: ["admin-users"] });
            }}
            onBlur={() => setEditingCompany(false)}
            data-testid={`company-select-${user.user_id}`}
            className="bg-surface-2 border border-border-strong rounded px-2 py-1 text-xs text-fg-muted max-w-[150px]"
          >
            <option value="">— none —</option>
            {companyOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.display_name}
              </option>
            ))}
          </select>
        ) : (
          <button
            onClick={() => setEditingCompany(true)}
            data-testid={`company-cell-${user.user_id}`}
            className="group/co inline-flex items-center gap-1.5 text-xs text-fg hover:text-white"
          >
            {(() => {
              const c = companyOptions.find((o) => o.id === user.company_id);
              return c ? (
                <span>{c.display_name}</span>
              ) : (
                <span className="text-fg-muted italic">Set company</span>
              );
            })()}
            <FaEdit className="w-2.5 h-2.5 text-fg-muted opacity-0 transition-opacity group-hover/co:opacity-100" />
          </button>
        )}
      </td>
      <td className="px-3 py-3">
        <InlineTagEditor
          values={effectiveCohorts}
          inheritedIds={inheritedCohortIds}
          options={cohortOptions}
          userType={user.user_type}
          grants={user.grants}
          grantType="cohort"
          onSave={async (newCohorts) => {
            await authService.updateUserCohorts(user.user_id, newCohorts);
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
          }}
        />
      </td>
      <td className="px-3 py-3">
        <InlineTagEditor
          values={user.allowed_projects}
          options={projectOptions}
          userType={user.user_type}
          useGrouped
          grants={user.grants}
          grantType="project"
          onSave={async (newProjects) => {
            await authService.updateUserProjects(user.user_id, newProjects);
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
          }}
        />
      </td>
      <td className="px-3 py-3">
        {user.disabled ? (
          <span className="text-xs text-fg-muted">Access disabled</span>
        ) : (
          <div className="flex flex-col gap-1.5">
            {isInternalRole(user.user_type) ? (
              <span
                className="text-xs text-fg-muted italic"
                title="Internal staff can always upload"
              >
                Upload: always
              </span>
            ) : (
              <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={capabilities.has("upload")}
                  disabled={savingCap}
                  data-testid={`upload-cap-${user.user_id}`}
                  onChange={() => saveCapability("upload", !capabilities.has("upload"))}
                />
                <span className="text-fg-muted">Upload</span>
              </label>
            )}
            {user.user_type === "admin" ? (
              <span className="text-xs text-fg-muted italic">Manage labeling: always</span>
            ) : (
              <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={capabilities.has("manage_labeling")}
                  disabled={savingCap}
                  data-testid={`manage-labeling-cap-${user.user_id}`}
                  onChange={() =>
                    saveCapability("manage_labeling", !capabilities.has("manage_labeling"))
                  }
                />
                <span className="text-fg-muted">Manage labeling</span>
              </label>
            )}
          </div>
        )}
      </td>
      <td className="px-3 py-3">
        {user.has_accepted_agreement ? (
          <div>
            <span className="text-xs text-signal-text-tint">Accepted</span>
            {user.agreement_accepted_at && (
              <div className="text-[10px] text-fg-muted">
                {new Date(user.agreement_accepted_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            )}
          </div>
        ) : user.disabled ? (
          <span className="text-xs text-fg-muted">Not accepted</span>
        ) : (
          <div className="flex flex-col items-start gap-1" data-testid="pending-invite-controls">
            <span className="text-xs font-bold text-fg">PENDING</span>
            {localToken && (
              <CopyInviteLinkButton token={localToken} email={user.email || user.user_id} />
            )}
            <ResendInviteButton
              userId={user.email || user.user_id}
              onResent={(newToken) => setLocalToken(newToken)}
            />
          </div>
        )}
      </td>
      <td className="px-3 py-3">
        <span className="text-xs text-fg-muted" title={user.invited_by || undefined}>
          {displayInvitedBy(user.invited_by)}
        </span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          {canAssume && (
            <div ref={assumeRef} className="relative">
              <button
                onClick={() => {
                  setAssumeError(null);
                  setAssumeOpen((prev) => !prev);
                }}
                disabled={assuming}
                data-testid={`assume-${user.user_id}`}
                className="px-2 py-1 text-xs text-fg hover:text-fg hover:bg-surface-2 border border-border-strong rounded transition-colors disabled:opacity-50"
                title="Act as this user — every action is tagged with your admin id"
              >
                {assuming ? <FaSpinner className="w-3 h-3 animate-spin" /> : "Assume"}
              </button>
              {assumeOpen && (
                <div
                  data-testid={`assume-confirm-${user.user_id}`}
                  className="absolute right-0 top-full mt-1 z-40 w-72 bg-bg-2 border border-border-strong rounded shadow-xl p-3"
                >
                  <p className="text-xs text-fg-muted mb-3">
                    Act as <span className="font-mono text-fg">{user.email || user.user_id}</span>?
                    Every action you take will be tagged with your admin account.
                  </p>
                  {assumeError && (
                    <p className="text-xs text-warn mb-2" data-testid="assume-error">
                      {assumeError}
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setAssumeOpen(false);
                        setAssumeError(null);
                      }}
                      disabled={assuming}
                      className="px-2 py-1 text-xs text-fg-muted hover:text-white rounded disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAssume}
                      disabled={assuming}
                      data-testid={`assume-confirm-btn-${user.user_id}`}
                      className="px-2 py-1 text-xs text-fg hover:text-fg border border-border-strong rounded disabled:opacity-50"
                    >
                      {assuming ? <FaSpinner className="w-3 h-3 animate-spin" /> : "Confirm"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {canToggleDisabled && (
            <button
              onClick={handleToggleDisabled}
              disabled={togglingDisabled}
              data-testid={`toggle-disabled-${user.user_id}`}
              className={`px-2 py-1 text-xs border border-border-strong rounded transition-colors disabled:opacity-50 ${user.disabled ? "text-fg hover:bg-surface-2" : "text-fg-muted hover:text-warn"}`}
              title={
                user.disabled
                  ? "Let this account sign in again"
                  : "Stop this account's sessions and sign-ins"
              }
            >
              {togglingDisabled ? (
                <FaSpinner className="w-3 h-3 animate-spin" />
              ) : user.disabled ? (
                "Enable"
              ) : (
                "Disable"
              )}
            </button>
          )}
          {!canAssume && !user.has_pending_invite && !canToggleDisabled && (
            <span className="text-xs text-fg-muted">—</span>
          )}
          {user.has_pending_invite && (
            <button
              onClick={async () => {
                if (deleting) return;
                if (!confirm(`Delete pending user ${user.email || user.user_id}?`)) return;
                setDeleting(true);
                try {
                  await authService.deleteUser(user.email || user.user_id);
                  queryClient.invalidateQueries({ queryKey: ["admin-users"] });
                } catch (err: unknown) {
                  const message = axios.isAxiosError(err) ? err.response?.data?.detail : null;
                  alert(message || "Failed to delete user");
                } finally {
                  setDeleting(false);
                }
              }}
              disabled={deleting}
              className="opacity-0 group-hover:opacity-100 p-1 text-fg-muted hover:text-warn transition-all disabled:opacity-50"
              title="Delete pending user"
            >
              {deleting ? (
                <FaSpinner className="w-3 h-3 animate-spin" />
              ) : (
                <FaTrash className="w-3 h-3" />
              )}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const InviteModal: React.FC<{
  projectOptions: string[];
  labelingCohortOptions: string[];
  readerCohortOptions: string[];
  companyOptions: Array<{ id: string; display_name: string }>;
  onClose: () => void;
  onSuccess: () => void;
}> = ({
  projectOptions,
  labelingCohortOptions,
  readerCohortOptions,
  companyOptions,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("annotator");
  const [selectedCohorts, setSelectedCohorts] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [allowUploads, setAllowUploads] = useState(false);
  const [ttlDays, setTtlDays] = useState(DEFAULT_INVITE_TTL_DAYS);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isReviewer = READER_COHORT_ROLES.has(role);
  const cohortOptions = isReviewer ? readerCohortOptions : labelingCohortOptions;

  const uploadMutation = useMutation({
    mutationFn: (invitedEmail: string) => authService.setUserCapabilities(invitedEmail, ["upload"]),
    onSuccess,
    onError: (err: unknown) => {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : null;
      setError(detail || "Failed to enable uploads.");
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const normalizedEmail = email.trim().toLowerCase();
      await authService.inviteExternalUser(
        normalizedEmail,
        role,
        selectedCohorts,
        selectedProjects,
        ttlDays,
        selectedCompanyId || undefined,
      );
      return normalizedEmail;
    },
    onSuccess: (invitedEmail) => {
      if (allowUploads) uploadMutation.mutate(invitedEmail);
      else onSuccess();
    },
    onError: (err: unknown) => {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : null;
      setError(detail || "Failed to send invite.");
    },
  });
  const isPending = inviteMutation.isPending || uploadMutation.isPending;

  // Reset cohorts when switching between role buckets so the admin doesn't
  // accidentally invite a reviewer with a labeling cohort id (or vice versa).
  const handleRoleChange = (nextRole: string) => {
    const nextIsReviewer = READER_COHORT_ROLES.has(nextRole);
    if (nextIsReviewer !== isReviewer) setSelectedCohorts([]);
    setRole(nextRole);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      data-testid="invite-external-user-modal"
    >
      {/* max-h + scroll: the form (email, role, company, cohorts, projects,
          TTL, footer) is taller than a laptop-height (720px) viewport, and
          without an internal scroll the footer buttons overflow below the
          fold — unreachable for the user, and the source of the intermittent
          "Cancel never clickable" E2E flake (#851). */}
      <div className="bg-bg-2 border border-border-strong rounded p-6 w-full max-w-md max-h-[calc(100vh-2rem)] overflow-y-auto shadow-2xl">
        <h2 className="text-lg font-medium mb-4">Invite External User</h2>

        {inviteMutation.data && (
          <p className="mb-4 text-sm text-fg">
            Invitation sent to {inviteMutation.data}. Upload permission still needs to be saved.
          </p>
        )}

        {error && (
          <Alert variant="destructive" className="mb-4 p-3 bg-warn-tint">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <fieldset disabled={isPending || Boolean(inviteMutation.data)} className="space-y-4">
          <div>
            <Label className="text-fg-muted text-sm">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="bg-bg-2/50 border-border-strong text-white mt-1"
            />
          </div>

          <div>
            <Label className="text-fg-muted text-sm">Role</Label>
            <select
              value={role}
              onChange={(e) => handleRoleChange(e.target.value)}
              data-testid="invite-role-select"
              className="w-full mt-1 px-3 py-2 bg-bg-2/50 border border-border-strong rounded text-white text-sm"
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-fg-muted text-sm">Company</Label>
            <select
              value={selectedCompanyId}
              onChange={(e) => setSelectedCompanyId(e.target.value)}
              data-testid="invite-company-select"
              className="w-full mt-1 px-3 py-2 bg-bg-2/50 border border-border-strong rounded text-white text-sm"
            >
              <option value="">— none —</option>
              {companyOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-fg-muted">
              Assigning a company grants its cohorts to the user on invite accept.
            </p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={allowUploads}
            data-testid="invite-allow-uploads"
            onClick={() => setAllowUploads((allowed) => !allowed)}
            className={`flex w-full items-center gap-3 rounded border px-3 py-2 text-left transition-colors ${
              allowUploads
                ? "border-signal-stroke bg-signal-tint text-fg"
                : "border-border-strong bg-bg-2 text-fg-muted hover:text-fg"
            }`}
          >
            <span
              aria-hidden="true"
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                allowUploads ? "border-signal bg-signal text-white" : "border-border-strong"
              }`}
            >
              {allowUploads && <FaCheck className="h-2.5 w-2.5" />}
            </span>
            <span>
              <span className="block text-sm font-medium">Allow uploads</span>
              <span className="block text-xs text-fg-muted">
                Let this user upload studies after accepting the invite.
              </span>
            </span>
          </button>

          <div>
            <Label className="text-fg-muted text-sm">
              {isReviewer ? "Reader cohorts (optional)" : "Cohorts (optional)"}
            </Label>
            <div className="mt-1">
              <TagSelect
                options={cohortOptions}
                selected={selectedCohorts}
                onChange={setSelectedCohorts}
                placeholder={isReviewer ? "cohort_id..." : "Select or type cohorts..."}
              />
            </div>
            {isReviewer && (
              <p className="mt-1 text-xs text-fg-muted">
                You can assign reader cohorts now or after the invitation is sent.
              </p>
            )}
          </div>

          {!isReviewer && (
            <div>
              <Label className="text-fg-muted text-sm">Projects (optional)</Label>
              <div className="mt-1">
                <GroupedProjectSelect
                  options={projectOptions}
                  selected={selectedProjects}
                  onChange={setSelectedProjects}
                  collapsible
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="invite-ttl" className="text-fg-muted text-sm">
              Link expires in (days)
            </Label>
            <Input
              id="invite-ttl"
              type="number"
              min={MIN_INVITE_TTL_DAYS}
              max={MAX_INVITE_TTL_DAYS}
              value={ttlDays}
              onChange={(e) => setTtlDays(Number(e.target.value))}
              className="bg-bg-2/50 border-border-strong text-white w-24 mt-1"
            />
          </div>
        </fieldset>

        <div className="mt-4 flex gap-3 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isPending} className="flex-1">
            {inviteMutation.data ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              if (inviteMutation.data) uploadMutation.mutate(inviteMutation.data);
              else inviteMutation.mutate();
            }}
            disabled={!email || isPending}
            className="flex-1"
          >
            {isPending ? (
              <>
                <FaSpinner className="animate-spin mr-2 h-3 w-3" />
                {inviteMutation.data ? "Enabling uploads..." : "Sending..."}
              </>
            ) : inviteMutation.data ? (
              "Retry uploads"
            ) : (
              "Send Invite"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagement;
